import {
  addDisplayName,
  addHandlers,
  addProps,
  addRef,
  addState,
  addWrapper,
  flowMax,
} from 'ad-hok'
import { ComponentProps, FC } from 'react'
import cx from 'classnames'
import {
  addDataLoadingFromPromise,
  hasData,
} from 'shared/src/utils/dataLoading'
import { createUseStyles } from 'react-jss'
import { ActionMeta } from 'react-select'
import CreatableReactSelect from 'react-select/async-creatable'
import ReactSelect from 'react-select/async'
import { addUser } from 'shared/src/api'
import { SelectInstance } from 'react-select'
import { addLayoutEffectOnMount } from 'ad-hok-utils'
import { typedAs } from 'shared/src/utils/typedAs'
import { ImageUrl } from 'shared/src/api/types'

import { ResourceName, ResourceType } from './resource'
import { Field } from './Field'
import { fontFamily, textColorLight } from './styles'
import { ResourceEdit } from './ResourceEdit'
import { addFormContext } from './formContext'
import { addResourceWithName } from '../utils/addResourceWithName'
import Icon from './Icon'

const styles = {
  container: cx('w-full', 'flex', 'flex-row', 'items-center'),
  select: cx('w-full', 'ring-0'),
  editButton: cx(
    'ml-5',
    'cursor-pointer',
    'text-rodeo-secondary1/56',
    'hover:text-rodeo-secondary1/96'
  ),
}

const useStyles = createUseStyles({
  form: {
    marginTop: 10,
  },
  option: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  thumbnailContainer: {
    height: 40,
    width: 40,
    display: 'flex',
    alignItems: 'center',
  },
  thumbnail: {
    objectFit: 'cover',
    marginRight: 10,
    height: '100%',
    width: '100%',
  },
})

interface Option {
  value: number
  image?: ImageUrl
  label: string
}

interface SelectProps extends ComponentProps<typeof Field> {
  resource: ResourceType<any, any, any>
  values: { id: number }[]
  selectedId: number | undefined
  onSelect: (id: number | undefined) => void
  onCreate?: (query?: string) => void
  onEdit: (id: number) => void
}

const Select: FC<SelectProps> = flowMax(
  addDisplayName('Select'),
  addHandlers({
    onChange:
      ({ onSelect, onCreate }) =>
      (option: Option | null, action: ActionMeta<Option>) => {
        if (action.action === 'select-option') {
          onSelect(option?.value)
        } else if (action.action === 'create-option' && onCreate) {
          onCreate(option?.label)
        } else if (action.action === 'clear') {
          onSelect(undefined)
        }
      },
    onEdit:
      ({ selectedId, onEdit }) =>
      () => {
        if (selectedId) {
          onEdit(selectedId)
        }
      },
  }),
  addHandlers({
    valueToOption:
      ({ resource }) =>
      (value: { id: number; image?: ImageUrl }) => ({
        value: value.id,
        image: value.image,
        label: resource.title(value),
      }),
  }),
  addProps(({ values, valueToOption }) => ({
    options: values.map(valueToOption),
  })),
  addProps(
    ({
      resource: {
        api: { search },
      },
      options,
      valueToOption,
    }) => ({
      loadOptions: search
        ? (query: string) =>
            search(query).then((values) => values.map(valueToOption))
        : async (query: string) =>
            options.filter(({ label }) =>
              label.toLowerCase().includes(query.toLowerCase())
            ),
    })
  ),
  addProps(({ options, selectedId }) => ({
    value: options.find(({ value }) => value === selectedId),
  })),
  addUser,
  addProps(({ user, resource }) => ({
    canEdit:
      user.isAdmin ||
      !!resource.availableToCreators ||
      !!resource.editableByCreators,
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  addRef('ref', typedAs<SelectInstance<Option> | null>(null)),
  addLayoutEffectOnMount(({ focusOnMount, ref }) => () => {
    if (focusOnMount) {
      ref.current?.focus()
    }
  }),
  ({
    selectedId,
    options,
    value,
    onChange,
    onCreate,
    onEdit,
    canEdit,
    loadOptions,
    disabled,
    classes,
    ref,
  }) => (
    <div className={styles.container}>
      {onCreate ? (
        <CreatableReactSelect
          defaultOptions={options}
          value={value}
          onChange={onChange}
          loadOptions={loadOptions}
          ref={ref}
          cacheOptions
          isClearable
          isDisabled={disabled}
          formatOptionLabel={({ image, label }) => (
            <div className={classes.option}>
              {image && (
                <div className={classes.thumbnailContainer}>
                  <img
                    src={image.thumbnail}
                    alt=""
                    className={classes.thumbnail}
                  />
                </div>
              )}
              <span>{label}</span>
            </div>
          )}
          className={styles.select}
          styles={{
            control: (provided) => ({
              ...provided,
              border: '1px solid rgba(0, 0, 0, 0.17)',
              borderRadius: 0,
              height: 46,
            }),
            valueContainer: (provided) => ({
              ...provided,
              paddingLeft: 19,
              paddingRight: 19,
            }),
            singleValue: (provided) => ({
              ...provided,
              color: textColorLight,
              fontSize: 14,
              fontFamily,
            }),
          }}
        />
      ) : (
        <ReactSelect
          defaultOptions={options}
          value={value}
          onChange={onChange}
          loadOptions={loadOptions}
          cacheOptions
          isClearable
          ref={ref}
          isDisabled={disabled}
          className={styles.select}
          styles={{
            control: (provided) => ({
              ...provided,
              border: '1px solid rgba(0, 0, 0, 0.17)',
              borderRadius: 0,
              height: 46,
            }),
            valueContainer: (provided) => ({
              ...provided,
              paddingLeft: 19,
              paddingRight: 19,
            }),
            singleValue: (provided) => ({
              ...provided,
              color: textColorLight,
              fontSize: 14,
              fontFamily,
            }),
          }}
        />
      )}
      {!selectedId && onCreate && (
        <Icon
          name="add"
          onClick={() => onCreate()}
          className={styles.editButton}
        />
      )}
      {selectedId && canEdit && (
        <Icon name="edit" onClick={onEdit} className={styles.editButton} />
      )}
    </div>
  )
)

interface Selecting {
  type: 'selecting'
}

interface Creating {
  type: 'creating'
  query?: string
}

interface Editing {
  type: 'editing'
  id: number
}

type State = Selecting | Creating | Editing

interface Props extends ComponentProps<typeof Field> {
  resourceName: ResourceName
  selectedId: number | undefined
  onChange: (id: number | undefined) => void
  disableCreate?: boolean
  filter?: (value: any) => boolean
  disabled?: boolean
}

export const ResourceSelectField: FC<Props> = flowMax(
  addDisplayName('ModelSelectField'),
  addResourceWithName(({ resourceName }) => resourceName),
  addState('state', 'setState', typedAs<State>({ type: 'selecting' })),
  addDataLoadingFromPromise(
    'values',
    ({ selectedId, resource: { api } }) =>
      api.search
        ? // if the resource supports searching, avoid an expensive
          // "all" query
          selectedId
          ? // get the selected value and combine it with the default
            // search results
            Promise.all([api.get(selectedId), api.search('')]).then(
              ([selected, searchResult]) => [
                selected,
                ...searchResult.filter(({ id }) => id !== selectedId),
              ]
            )
          : // get the default search results
            api.search('')
        : api.getAll(),
    ['resource', 'state.type']
  ),
  addProps(({ values }) => ({
    values: hasData(values) ? values.data : [],
  })),
  addProps(({ values, filter }) => ({
    values: filter ? values.filter(filter) : values,
  })),
  addFormContext,
  addHandlers({
    onSelect:
      ({ onChange, setFormDisabled }) =>
      (id: number | undefined) => {
        onChange(id)
        setFormDisabled(false)
      },
    onCreate:
      ({ setState, setFormDisabled }) =>
      (query?: string) => {
        setState({ type: 'creating', query })
        setFormDisabled(true)
      },
    onEdit:
      ({ setState, setFormDisabled }) =>
      (id: number) => {
        setState({ type: 'editing', id })
        setFormDisabled(true)
      },
    onCreated:
      ({ setState, values, setValues, onChange, setFormDisabled }) =>
      (model: any) => {
        setState({ type: 'selecting' })
        setValues([model, ...values])
        onChange(model.id)
        setFormDisabled(false)
      },
    onSaved:
      ({ setState, values, setValues, onChange, setFormDisabled }) =>
      (model: any) => {
        setState({ type: 'selecting' })
        setValues(
          values.map((value) => (value.id === model.id ? model : value))
        )
        onChange(model.id)
        setFormDisabled(false)
      },
    onCancel:
      ({ setState, setFormDisabled }) =>
      () => {
        setState({ type: 'selecting' })
        setFormDisabled(false)
      },
  }),
  addWrapper((render, { id, label, error, state, suggestion }) => (
    <Field
      id={id}
      label={label}
      error={error}
      ignoreDisabled={state.type !== 'selecting'}
      suggestion={suggestion}
    >
      {render()}
    </Field>
  )),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    state,
    id,
    label,
    error,
    selectedId,
    values,
    resource,
    onSelect,
    onCreate,
    onEdit,
    onCreated,
    onSaved,
    onCancel,
    disableCreate,
    disabled,
    classes,
    focusOnMount,
  }) => {
    if (state.type === 'selecting') {
      return (
        <Select
          id={id}
          label={label}
          error={error}
          selectedId={selectedId}
          values={values}
          resource={resource}
          onSelect={onSelect}
          onCreate={disableCreate ? undefined : onCreate}
          onEdit={onEdit}
          focusOnMount={focusOnMount}
          disabled={disabled}
        />
      )
    } else if (state.type === 'editing') {
      return (
        <div className={classes.form}>
          <ResourceEdit
            resource={resource}
            resourceId={selectedId}
            embedded
            onSave={onSaved}
            onCancel={onCancel}
          />
        </div>
      )
    } else {
      return (
        <div className={classes.form}>
          <ResourceEdit
            resource={resource}
            creatingQuery={state.query}
            embedded
            onSave={onCreated}
            onCancel={onCancel}
          />
        </div>
      )
    }
  }
)
